import React from 'react';
import { View, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { Text } from '@/components';
import { Image } from 'expo-image';
import { Dog } from 'lucide-react-native';
import { useDogs } from '@/context/DogsContext';
import { useSelectedDog } from '@/context/SelectedDogContext';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';

/** Horizontal row of dog avatars to choose which dog trail matching uses. */
export default function DogPicker({ compact = false }: { compact?: boolean }) {
  const { dogProfiles } = useDogs();
  const { dogId, setDogId } = useSelectedDog();

  if (dogProfiles.length <= 1) return null; // nothing to choose between

  const activeId = dogId ?? dogProfiles[0]?.id;

  return (
    <View style={[styles.container, compact && styles.compact]}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.scroll}>
        {dogProfiles.map((d) => {
          const active = d.id === activeId;
          return (
            <TouchableOpacity
              key={d.id}
              style={[styles.chip, active && styles.chipActive]}
              onPress={() => setDogId(d.id)}
            >
              {d.image ? (
                <Image source={d.image} style={styles.avatar} contentFit="cover" cachePolicy="memory-disk" />
              ) : (
                <View style={[styles.avatar, styles.avatarPlaceholder]}>
                  <Dog size={16} color={theme.accentPrimary} />
                </View>
              )}
              <Text style={[styles.name, active && styles.nameActive]} numberOfLines={1}>
                {d.name}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { paddingVertical: 8 },
  compact: { paddingVertical: 4 },
  scroll: { paddingHorizontal: 16, gap: 8 },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: theme.backgroundSecondary,
    borderWidth: 1,
    borderColor: theme.borderSubtle,
  },
  chipActive: {
    borderColor: theme.accentPrimary,
    backgroundColor: theme.backgroundSecondaryVarient,
  },
  avatar: { width: 24, height: 24, borderRadius: 12 },
  avatarPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.backgroundSecondaryVarient,
  },
  name: { ...Typography.body(theme.textMuted), fontSize: 13 },
  nameActive: { color: theme.textPrimary, fontWeight: '600' },
});